import { Component } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { getStoredUser } from '../helpers/user-storage.util';
import { CommonModule } from '@angular/common';
import { NotificationDialogComponent } from '../notification-dialog/notification-dialog.component';
import { User } from '../model/user';
import { UserService } from '../services/user.service';
import { NotificationService } from '../services/notification.service';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '../services/project.service';
import { ToastrService } from 'ngx-toastr';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';

@Component({
  standalone: true,
  selector: 'page-header',
  imports: [
    RouterLink,
    CommonModule,
    NotificationDialogComponent,
    MatBadgeModule,
    MatMenuModule,
    FormsModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class Header {
  notificationsActive: boolean = false;
  userProfilePic: String = '';
  private destroy$ = new Subject<void>();

  user: User = {};
  readNotifications: any = [];
  unreadNotifications: any = [];

  searchTerm: string = '';

  get isInternal(): boolean {
    return getStoredUser().role === 'INTERNAL';
  }

  constructor(
    private userService: UserService,
    private notificationService: NotificationService,
    private projectService: ProjectService,
    private toastr: ToastrService,
    private authenticationService: AuthenticationService
  ) {}

  logout(): void {
    this.authenticationService.logout();
  }

  ngOnInit(): void {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      this.user = <User>JSON.parse(userJson);
      if (this.user && this.user.photo) {
        this.userProfilePic = this.user.photo;
      }
      if (!this.isInternal) {
        this.fetchNotifications();
      }
    }
    this.userService.getUser().subscribe((user) => {
      if (user) {
        this.userProfilePic = user.photo;
      }
    });
    this.projectService.showClearSearchButton.subscribe(
      (showButton: boolean) => {
        if (!showButton) {
          this.searchTerm = '';
        }
      }
    );

    if (!this.isInternal) {
      interval(300000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.fetchNotifications();
        });
    }
  }

  fetchNotifications() {
    if (this.user?.id) {
      this.notificationService
        .getVendorNotifications(this.user.id)
        .subscribe((result: any) => {
          this.processNotificationResponse(result);
        });
    }
  }

  markAllAsRead() {
    this.notificationService
      .markNotificationRead(this.user.id ?? '', this.unreadNotifications)
      .subscribe((result: any) => {
        this.processNotificationResponse(result);
      });
  }

  markAsRead(notification: any) {
    if (notification.status === 'UNREAD') {
      this.notificationService
        .markNotificationRead(this.user.id ?? '', [notification])
        .subscribe((result: any) => {
          this.processNotificationResponse(result);
        });
    }
  }

  markAsUnread(notification: any) {
    if (notification.status === 'READ') {
      this.notificationService
        .markNotificationUnread(this.user.id ?? '', notification)
        .subscribe((result: any) => {
          this.processNotificationResponse(result);
        });
    }
  }

  search() {
    if (this.searchTerm?.length !== 0) {
      this.toastr.info('Searching for address...');
      this.projectService.fetchSearchData(
        this.user.id ?? '',
        this.searchTerm,
        true
      );
      this.projectService.toggleClearSearchButton(true);
    }
  }

  toggleNotificationsModal() {
    this.notificationsActive = !this.notificationsActive;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processNotificationResponse(result: any) {
    if (result.length > 0) {
      this.unreadNotifications = result.filter(
        (notification: any) => notification.status === 'UNREAD'
      );
      this.readNotifications = result.filter(
        (notification: any) => notification.status === 'READ'
      );
    }
  }
}
